/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@material-ui/core"
import {
  SwapHoriz as SwapHorizIcon,
  Face as FaceIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  SkipNext as SkipIcon,
  FiberNew as NewIcon,
} from "@material-ui/icons"
import { useSnackbar } from "notistack"
import {
  getDeviceImagesOnce,
  migrateRostrosFirebase,
} from "src/services/admin/biometrics"
import * as S from "./MigrateRostrosDialog.styles"

const STEPS = ["Seleccionar destino", "Revisar rostros", "Migrar", "Resultado"]

const DeviceCard = ({ device, label, active = true }) => (
  <Box style={S.deviceCard(active)}>
    <Typography style={S.sectionLabel}>{label}</Typography>
    <Box style={S.deviceCardHeader}>
      <Avatar style={S.deviceAvatar(active)}>
        <SecurityIcon style={{ fontSize: 20 }} />
      </Avatar>
      <Box>
        <Typography style={S.deviceName}>
          {device?.nombre || device?.device_name || "—"}
        </Typography>
        <Typography style={S.deviceIp}>
          {device?.ip}:{device?.puerto || 80}
        </Typography>
      </Box>
    </Box>
    <Box
      display="flex"
      flexWrap="wrap"
      style={{ gap: "6px", marginTop: "8px" }}
    >
      <Chip
        size="small"
        label={device?.ubicacion || "Sin ubicación"}
        style={{ fontSize: "0.72rem", height: 20 }}
      />
      <Chip
        size="small"
        label={device?.marca || "Desconocido"}
        style={{
          fontSize: "0.72rem",
          height: 20,
          backgroundColor: "#e3f2fd",
          color: "#1565c0",
        }}
      />
    </Box>
  </Box>
)

const RostroRow = ({ rostro, variant = "new" }) => {
  const isNew = variant === "new"
  const borderColor = isNew ? "#bbf7d0" : "#fed7aa"
  const bgColor = isNew ? "#f0fdf4" : "#fff7ed"

  return (
    <Box
      style={{
        ...S.rostroListItem(rostro.is_sync),
        border: `1px solid ${borderColor}`,
        backgroundColor: bgColor,
        marginBottom: 4,
      }}
    >
      {rostro.imagen_url ? (
        <img
          src={rostro.imagen_url}
          alt={rostro.nombre}
          style={S.rostroAvatar}
        />
      ) : (
        <Box style={S.rostroAvatarPlaceholder}>
          <FaceIcon style={{ fontSize: 24, color: "#94a3b8" }} />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {rostro.nombre}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {rostro.identificacion}
        </Typography>
      </Box>
      <Box display="flex" style={{ gap: "4px" }}>
        <Chip
          size="small"
          label={rostro.tipo || "—"}
          style={{
            fontSize: "0.65rem",
            height: 18,
            textTransform: "capitalize",
          }}
        />
        {isNew ? (
          <Chip
            size="small"
            label="Nuevo"
            style={{
              fontSize: "0.65rem",
              height: 18,
              backgroundColor: "#dcfce7",
              color: "#166534",
            }}
          />
        ) : (
          <Chip
            size="small"
            label="Ya existe"
            style={{
              fontSize: "0.65rem",
              height: 18,
              backgroundColor: "#ffedd5",
              color: "#9a3412",
            }}
          />
        )}
      </Box>
    </Box>
  )
}

const MigrateRostrosDialog = ({
  open,
  onClose,
  sourceDevice,
  allDevices = [],
  conjuntoId,
}) => {
  const { enqueueSnackbar } = useSnackbar()

  const [activeStep, setActiveStep] = useState(0)
  const [targetDeviceId, setTargetDeviceId] = useState("")
  const [sourceRostros, setSourceRostros] = useState([])
  const [rostrosToMigrate, setRostrosToMigrate] = useState([])
  const [rostrosExisting, setRostrosExisting] = useState([])
  const [loadingRostros, setLoadingRostros] = useState(false)
  const [searchRostro, setSearchRostro] = useState("")
  const [diffTab, setDiffTab] = useState(0)
  const [migrating, setMigrating] = useState(false)
  const [migrationProgress, setMigrationProgress] = useState(0)
  const [migrationTotal, setMigrationTotal] = useState(0)
  const [migrationDone, setMigrationDone] = useState(0)
  const [results, setResults] = useState({
    successful: [],
    failed: [],
    skipped: [],
  })

  const targetDevice =
    allDevices.find(
      d => (d.rostro_id || d.id || d.dispositivo_id) === targetDeviceId
    ) || null

  const activeList = diffTab === 0 ? rostrosToMigrate : rostrosExisting
  const filteredActive = activeList.filter(
    r =>
      r.nombre?.toLowerCase().includes(searchRostro.toLowerCase()) ||
      r.identificacion?.includes(searchRostro)
  )

  // ── Reset al cerrar ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setActiveStep(0)
      setTargetDeviceId("")
      setSourceRostros([])
      setRostrosToMigrate([])
      setRostrosExisting([])
      setSearchRostro("")
      setDiffTab(0)
      setMigrationProgress(0)
      setMigrationTotal(0)
      setMigrationDone(0)
      setResults({ successful: [], failed: [], skipped: [] })
    }
  }, [open])

  const fetchRostros = useCallback(async () => {
    if (!sourceDevice || !conjuntoId || !targetDeviceId) return

    const srcId = sourceDevice.dispositivo_id || sourceDevice.id
    setLoadingRostros(true)

    try {
      const [srcList, tgtList] = await Promise.all([
        getDeviceImagesOnce(conjuntoId, srcId),
        getDeviceImagesOnce(conjuntoId, targetDeviceId),
      ])

      const existingIds = new Set(
        tgtList.map(img => img.identificacion).filter(Boolean)
      )

      const toMigrate = srcList.filter(r => !existingIds.has(r.identificacion))
      const existing = srcList.filter(r => existingIds.has(r.identificacion))

      setSourceRostros(srcList)
      setRostrosToMigrate(toMigrate)
      setRostrosExisting(existing)
      setDiffTab(0)
    } catch {
      enqueueSnackbar("Error al obtener las imágenes del dispositivo", {
        variant: "error",
      })
      setSourceRostros([])
      setRostrosToMigrate([])
      setRostrosExisting([])
    } finally {
      setLoadingRostros(false)
    }
  }, [sourceDevice, conjuntoId, targetDeviceId, enqueueSnackbar])

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!targetDeviceId) {
        enqueueSnackbar("Debe seleccionar un dispositivo destino", {
          variant: "warning",
        })
        return
      }
      await fetchRostros()
      setActiveStep(1)
    } else if (activeStep === 1) {
      if (rostrosToMigrate.length === 0) {
        enqueueSnackbar("No hay rostros nuevos para migrar", {
          variant: "warning",
        })
        return
      }
      setActiveStep(2)
      await handleMigrate()
    }
  }

  const handleMigrate = async () => {
    setMigrating(true)
    setMigrationTotal(rostrosToMigrate.length)
    setMigrationDone(0)
    setMigrationProgress(0)

    const sourceId = sourceDevice?.dispositivo_id || sourceDevice?.id

    try {
      const { successful, failed, skipped } = await migrateRostrosFirebase(
        conjuntoId,
        sourceId,
        targetDeviceId,
        (done, total) => {
          setMigrationDone(done)
          setMigrationProgress(Math.round((done / total) * 100))
        }
      )

      setResults({ successful, failed, skipped })
      setActiveStep(3)

      if (failed.length === 0) {
        enqueueSnackbar(
          `✅ ${successful.length} migrados · ${skipped.length} omitidos (ya existían)`,
          { variant: "success" }
        )
      } else {
        enqueueSnackbar(
          `⚠️ ${successful.length} exitosos · ${failed.length} fallidos · ${skipped.length} omitidos`,
          { variant: "warning" }
        )
      }
    } catch (err) {
      enqueueSnackbar(
        `Error en migración: ${err?.message || "Error desconocido"}`,
        { variant: "error" }
      )
      setActiveStep(3)
    } finally {
      setMigrating(false)
    }
  }

  const sourceId = sourceDevice?.dispositivo_id || sourceDevice?.id

  const destinationOptions = allDevices.filter(
    d =>
      (d.rostro_id || d.id || d.dispositivo_id) !== sourceId &&
      (d.status === "active" || d.estado === "activo")
  )

  return (
    <Dialog
      open={open}
      onClose={migrating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: S.dialogPaper }}
    >
      <DialogTitle style={S.dialogTitle} disableTypography>
        <Box style={S.titleBox}>
          <SwapHorizIcon style={{ fontSize: 28 }} />
          <Box flex={1}>
            <Typography
              variant="h6"
              style={{ fontWeight: 700, color: "white" }}
            >
              Migrar Rostros entre Dispositivos
            </Typography>
            <Typography
              variant="caption"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              Solo se copian los rostros que no existen en el dispositivo
              destino
            </Typography>
          </Box>
          {!migrating && (
            <IconButton
              size="small"
              onClick={onClose}
              style={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <Box style={S.stepperBox}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent style={S.contentPadding}>
        {activeStep === 0 && (
          <Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <DeviceCard
                  device={sourceDevice}
                  label="📤 Dispositivo origen"
                  active
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box style={S.arrowBox}>
                  <ArrowForwardIcon style={S.arrowIcon} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Box style={S.deviceCard(Boolean(targetDevice))}>
                  <Typography style={S.sectionLabel}>
                    📥 Dispositivo destino
                  </Typography>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Seleccionar dispositivo</InputLabel>
                    <Select
                      value={targetDeviceId}
                      onChange={e => setTargetDeviceId(e.target.value)}
                      label="Seleccionar dispositivo"
                    >
                      {destinationOptions.length === 0 ? (
                        <MenuItem disabled>
                          No hay otros dispositivos activos
                        </MenuItem>
                      ) : (
                        destinationOptions.map(d => {
                          const dId = d.rostro_id || d.id || d.dispositivo_id
                          return (
                            <MenuItem key={dId} value={dId}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 600 }}
                                >
                                  {d.device_name || d.nombre}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {d.ip} — {d.ubicacion}
                                </Typography>
                              </Box>
                            </MenuItem>
                          )
                        })
                      )}
                    </Select>
                  </FormControl>
                  {targetDevice && (
                    <Box
                      mt={1}
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: "6px" }}
                    >
                      <Chip
                        size="small"
                        label={targetDevice.ubicacion || "Sin ubicación"}
                        style={{ fontSize: "0.72rem", height: 20 }}
                      />
                      <Chip
                        size="small"
                        label={targetDevice.marca || "Desconocido"}
                        style={{
                          fontSize: "0.72rem",
                          height: 20,
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box style={S.warningBox}>
              <Box
                display="flex"
                alignItems="flex-start"
                style={{ gap: "10px" }}
              >
                <WarningIcon
                  style={{ color: "#f57c00", fontSize: 20, marginTop: 2 }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 700, color: "#e65100" }}
                  >
                    ¿Qué hace esta operación?
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                  >
                    Compara los rostros de ambos dispositivos por{" "}
                    <strong>cédula/identificación</strong>. Solo se copian los
                    que <strong>no existen en el destino</strong>. Los
                    duplicados se omiten automáticamente. La imagen S3{" "}
                    <strong>no se re-sube</strong> — se reutiliza el mismo link.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Grid container spacing={1} style={{ marginBottom: "12px" }}>
              <Grid item xs={5}>
                <Typography variant="caption" style={S.sectionLabel}>
                  Origen
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {sourceDevice?.device_name || sourceDevice?.nombre}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {sourceDevice?.ip}
                </Typography>
              </Grid>
              <Grid
                item
                xs={2}
                style={{ textAlign: "center", paddingTop: "20px" }}
              >
                <ArrowForwardIcon style={{ color: "#092147" }} />
              </Grid>
              <Grid item xs={5}>
                <Typography variant="caption" style={S.sectionLabel}>
                  Destino
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {targetDevice?.device_name || targetDevice?.nombre}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {targetDevice?.ip}
                </Typography>
              </Grid>
            </Grid>

            {!loadingRostros && (
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: "10px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Total en origen: <strong>{sourceRostros.length}</strong>
                </Typography>
                <Box
                  style={{ width: 1, height: 16, backgroundColor: "#e2e8f0" }}
                />
                <Chip
                  size="small"
                  icon={<NewIcon style={{ fontSize: 14 }} />}
                  label={`${rostrosToMigrate.length} por migrar`}
                  style={{
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                />
                <Chip
                  size="small"
                  icon={<SkipIcon style={{ fontSize: 14 }} />}
                  label={`${rostrosExisting.length} ya en destino`}
                  style={{
                    backgroundColor: "#ffedd5",
                    color: "#9a3412",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            )}

            <Divider style={{ marginBottom: "10px" }} />

            <Box
              style={{
                borderBottom: "1px solid #e0e0e0",
                marginBottom: "10px",
              }}
            >
              <Tabs
                value={diffTab}
                onChange={(_, v) => {
                  setDiffTab(v)
                  setSearchRostro("")
                }}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <span>✅ Por migrar</span>
                      <Chip
                        size="small"
                        label={rostrosToMigrate.length}
                        style={{
                          height: 18,
                          fontSize: "0.65rem",
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                        }}
                      />
                    </Box>
                  }
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    minHeight: 40,
                  }}
                />
                <Tab
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <span>⏭ Ya en destino</span>
                      <Chip
                        size="small"
                        label={rostrosExisting.length}
                        style={{
                          height: 18,
                          fontSize: "0.65rem",
                          backgroundColor: "#ffedd5",
                          color: "#9a3412",
                        }}
                      />
                    </Box>
                  }
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    minHeight: 40,
                  }}
                />
              </Tabs>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              style={{ gap: "8px", marginBottom: "10px" }}
            >
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Buscar por nombre o identificación..."
                value={searchRostro}
                onChange={e => setSearchRostro(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Recargar desde Firebase">
                <IconButton
                  size="small"
                  onClick={fetchRostros}
                  disabled={loadingRostros}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {loadingRostros ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <CircularProgress size={32} style={{ color: "#092147" }} />
                <Typography variant="body2" style={{ marginLeft: "12px" }}>
                  Comparando ambos dispositivos...
                </Typography>
              </Box>
            ) : filteredActive.length === 0 ? (
              <Box style={S.emptyState}>
                <FaceIcon
                  style={{
                    fontSize: 48,
                    color: "#e0e0e0",
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="body2">
                  {searchRostro
                    ? "No se encontraron rostros con ese filtro"
                    : diffTab === 0
                      ? "Todos los rostros del origen ya existen en el destino"
                      : "Ningún rostro del origen existe en el destino"}
                </Typography>
              </Box>
            ) : (
              <Box
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {filteredActive.map(rostro => (
                  <RostroRow
                    key={rostro.id}
                    rostro={rostro}
                    variant={diffTab === 0 ? "new" : "existing"}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* STEP 2 — Progreso */}
        {activeStep === 2 && (
          <Box style={S.progressBox}>
            <CircularProgress
              size={60}
              style={{ color: "#092147", marginBottom: "20px" }}
            />
            <Typography
              variant="h6"
              style={{ fontWeight: 700, marginBottom: "8px" }}
            >
              Migrando rostros...
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: "20px" }}
            >
              <br />
              Por favor no cierre esta ventana.
            </Typography>
            <Box style={S.progressBarBox}>
              <LinearProgress
                variant="determinate"
                value={migrationProgress}
                style={{ height: "8px", borderRadius: "4px" }}
              />
            </Box>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: "8px" }}
            >
              {migrationDone} / {migrationTotal} rostros procesados (
              {migrationProgress}%)
            </Typography>
          </Box>
        )}

        {/* STEP 3 — Resultado */}
        {activeStep === 3 && (
          <Box>
            <Box style={S.resultsSummary(results.failed?.length === 0)}>
              {results.failed?.length === 0 ? (
                <CheckCircleIcon style={{ color: "#16a34a", fontSize: 32 }} />
              ) : (
                <WarningIcon style={{ color: "#ea580c", fontSize: 32 }} />
              )}
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  {results.failed?.length === 0
                    ? "¡Migración completada!"
                    : "Migración parcialmente completada"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ✅ {results.successful?.length} migrados
                  {results.skipped?.length > 0 &&
                    ` · ⏭ ${results.skipped.length} omitidos (ya existían)`}
                  {results.failed?.length > 0 &&
                    ` · ❌ ${results.failed.length} fallidos`}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={1} style={{ marginBottom: "16px" }}>
              <Grid item xs={5}>
                <Paper
                  elevation={0}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography variant="caption" style={S.sectionLabel}>
                    Origen
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {sourceDevice?.device_name || sourceDevice?.nombre}
                  </Typography>
                </Paper>
              </Grid>
              <Grid
                item
                xs={2}
                style={{ textAlign: "center", paddingTop: "24px" }}
              >
                <ArrowForwardIcon style={{ color: "#16a34a" }} />
              </Grid>
              <Grid item xs={5}>
                <Paper
                  elevation={0}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#f0fdf4",
                  }}
                >
                  <Typography variant="caption" style={S.sectionLabel}>
                    Destino
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {targetDevice?.device_name || targetDevice?.nombre}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Omitidos */}
            {results.skipped?.length > 0 && (
              <Box mb={2}>
                <Typography
                  variant="caption"
                  style={{ ...S.sectionLabel, color: "#b45309" }}
                >
                  Omitidos — ya existen en destino ({results.skipped.length})
                </Typography>
                <Box
                  style={{
                    maxHeight: "120px",
                    overflowY: "auto",
                    marginTop: "6px",
                  }}
                >
                  {results.skipped.map(rostro => (
                    <Box
                      key={rostro.id}
                      display="flex"
                      alignItems="center"
                      style={{ gap: "8px", marginBottom: "4px" }}
                    >
                      <SkipIcon style={{ color: "#d97706", fontSize: 16 }} />
                      <Typography variant="body2">
                        {rostro.nombre} —{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                        >
                          {rostro.identificacion}
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Fallidos */}
            {results.failed?.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  style={{ ...S.sectionLabel, color: "#dc2626" }}
                >
                  Fallidos ({results.failed.length})
                </Typography>
                <Box
                  style={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    marginTop: "6px",
                  }}
                >
                  {results.failed.map(rostro => (
                    <Box
                      key={rostro.id}
                      display="flex"
                      alignItems="center"
                      style={{ gap: "8px", marginBottom: "4px" }}
                    >
                      <ErrorIcon style={{ color: "#f44336", fontSize: 16 }} />
                      <Typography variant="body2">
                        {rostro.nombre} —{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                        >
                          {rostro.error}
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions style={S.dialogActions}>
        <Button
          onClick={onClose}
          disabled={migrating}
          style={S.cancelButton}
          variant="outlined"
        >
          {activeStep === 3 ? "Cerrar" : "Cancelar"}
        </Button>
        {activeStep < 2 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              loadingRostros ||
              migrating ||
              (activeStep === 0 && !targetDeviceId) ||
              (activeStep === 1 && rostrosToMigrate.length === 0)
            }
            style={S.migrateButton}
            startIcon={
              activeStep === 1 ? <SwapHorizIcon /> : <ArrowForwardIcon />
            }
          >
            {activeStep === 0
              ? "Comparar dispositivos"
              : `Migrar ${rostrosToMigrate.length} rostro${rostrosToMigrate.length !== 1 ? "s" : ""} nuevo${rostrosToMigrate.length !== 1 ? "s" : ""}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default MigrateRostrosDialog
